import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Listbox } from '@headlessui/react'
import classNames from 'classnames'
import { Else, If, Then } from 'react-if'
import { MagnifyingGlassIcon } from '@/atoms/Icons/MagnifyingGlassIcon'
import { LegacyImage as Image } from '@/atoms/LegacyImage'
import { RegionOption } from '@/molecules/RegionSelector/RegionOption'
import useCountrySelectorList from '@/molecules/RegionSelector/useCountrySelectorList'
import { TheatricalReleaseRegion } from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'

export interface LanguageMenuProps {
  className?: string
  isOpen?: boolean
  isDarkMode?: boolean
  regions: TheatricalReleaseRegion[]
  onOpen?: () => void
  onClose: () => void
  isMobileNavItem?: boolean
  onRegionModalOpen: () => void
  onLanguageModalClose?: () => void
  closeMobileMenu?: () => void
  showDefaultMobileNav?: boolean
}

export const RegionSelectorComponent: FC<LanguageMenuProps> = ({
  className,
  isOpen,
  isDarkMode,
  regions,
  onOpen,
  onClose,
  isMobileNavItem = false,
  onRegionModalOpen,
  onLanguageModalClose,
  closeMobileMenu,
  showDefaultMobileNav,
}) => {
  const { t } = useTranslate('account')
  const track = useSafeTrack()
  const { pathname, asPath, push, query } = useSafeRouter()
  const backgroundClasses = isDarkMode ? 'bg-core-gray-900' : 'bg-white'
  const flag = (query?.region as string) ?? 'us'
  const [value, setValue] = useState<string>('')
  const countryPickerRef = useRef<HTMLDivElement>(null)
  const countryFlagButtonRef = useRef<HTMLButtonElement>(null)
  const { countries, isRegionSelected } = useCountrySelectorList({
    regions,
    inputValue: value,
  })

  const handleClose = useCallback(() => {
    setValue('')
    onClose()
  }, [onClose])

  useEffect(() => {
    const closeCountryPickerOnClick = (e: MouseEvent) => {
      if (
        countryPickerRef.current &&
        !countryPickerRef?.current.contains(e.target as Node) &&
        countryFlagButtonRef &&
        !countryFlagButtonRef?.current?.contains(e.target as Node)
      ) {
        handleClose()
      }
    }
    document.addEventListener('click', closeCountryPickerOnClick)

    return () => {
      document.removeEventListener('click', closeCountryPickerOnClick)
    }
  }, [handleClose])
  const handleOnChange = (country: string) => {
    handleClose()
    track('Region Selected', country)
    const slug = query?.slug as string
    if (!slug) return
    const path = pathname.includes('tickets') ? `/tickets/${slug}` : `/pay-it-forward/${slug}`
    const queryParams = asPath.split('?')[1]
    if (queryParams) return push(`${path}/${country?.toLowerCase()}?${queryParams}`)
    return push(`${path}/${country?.toLowerCase()}`)
  }
  return (
    <div className={classNames(className, showDefaultMobileNav && 'hidden md:block')}>
      <If condition={isMobileNavItem}>
        <Then>
          <div
            onClick={() => {
              onRegionModalOpen()
              if (onLanguageModalClose) onLanguageModalClose()
              if (closeMobileMenu) closeMobileMenu()
            }}
            className={classNames(
              'whitespace-nowrap block p-1',
              'rounded-md px-2 py-1.5 cursor-pointer bg-transparent transition-bg duration-200',
              isDarkMode ? 'hover:bg-[rgb(255,255,255,0.1)]' : 'hover:bg-[rgb(0,0,0,0.06)]',
            )}
          >
            {flag && (
              <div className="relative h-[18px] w-[24px]">
                <Image
                  style={{ aspectRatio: '4/3' }}
                  alt="country"
                  src={`${flag}.svg`}
                  objectFit="cover"
                  width="24"
                  layout="fill"
                  loader={({ src }) => `https://flagcdn.com/${src}`}
                />
              </div>
            )}
          </div>
        </Then>
        <Else>
          <Listbox className="relative" as="div" onChange={handleOnChange} value={'US'} aria-label="select language">
            <Listbox.Button
              ref={countryFlagButtonRef}
              className={classNames('p-2')}
              onClick={() => {
                isOpen ? handleClose() : onOpen && onOpen()
              }}
            >
              {flag && (
                <div className="relative h-[18px] w-[24px]">
                  <Image
                    style={{ aspectRatio: '4/3' }}
                    alt="country"
                    src={`${flag}.svg`}
                    objectFit="cover"
                    width="24"
                    layout="fill"
                    loader={({ src }) => `https://flagcdn.com/${src}`}
                  />
                </div>
              )}
            </Listbox.Button>
            {isOpen && (
              <>
                <div className="absolute -right-0 top-10 h-[2px] w-10 bg-oxide-700"></div>
                <div
                  ref={countryPickerRef}
                  className={classNames(
                    'absolute top-10 -right-0 min-w-[250px] max-h-[85vh] mt-4 rounded-lg overflow-y-auto',
                    backgroundClasses,
                  )}
                  style={{
                    boxShadow: '0px -4px 16px -1px rgba(0, 0, 0, 0.05), 0px 8px 16px -1px rgba(0, 0, 0, 0.12)',
                  }}
                >
                  <Listbox.Options className="flex max-h-[450px] min-h-[450px] flex-col overflow-hidden" static>
                    <div
                      className={classNames(
                        'border border-x-transparent border-t-transparent sticky top-0 z-10',
                        isDarkMode ? 'bg-core-gray-900 border-b-core-gray-950' : 'bg-white border-b-core-gray-200',
                      )}
                    >
                      <input
                        className={classNames(
                          'w-full bg-transparent p-2 pl-10 text-core-gray-950 ring-0 ring-transparent py-4',
                          isDarkMode ? 'placeholder:text-core-gray-400 text-white' : 'placeholder:text-core-gray-500',
                        )}
                        value={value}
                        placeholder={t('regionSelectorSearchPlaceholder', 'Search Location...')}
                        onChange={(evt) => {
                          setValue(evt.target.value)
                        }}
                      />
                      <MagnifyingGlassIcon
                        stroke={1.5}
                        size={22}
                        className="absolute left-3 top-1/2 -translate-y-1/2"
                        color="core-gray-700"
                      />
                    </div>
                    <div className="grow-1 overflow-scroll pt-1 scrollbar-corner-black">
                      {countries.map((country) => (
                        <Listbox.Option
                          className="h-full"
                          key={country?.countryCode}
                          value={country?.countryCode?.toUpperCase()}
                        >
                          {() => (
                            <RegionOption
                              countryCode={country?.countryCode as string}
                              isDarkMode={isDarkMode}
                              countryName={country?.country as string}
                              selected={
                                country?.countryCode ? isRegionSelected(country?.countryCode?.toUpperCase()) : false
                              }
                            />
                          )}
                        </Listbox.Option>
                      ))}
                    </div>
                  </Listbox.Options>
                </div>
              </>
            )}
          </Listbox>
        </Else>
      </If>
    </div>
  )
}
